import { emitterEvents } from './BaseImagingProvider';
import type { BaseImagingProviderArgs } from './BaseImagingProvider';
import colors from 'styles/colors';
import { LRUMap } from 'lru_map';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type { DreAllViewportTypes } from 'config/constants';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import {
  type FrameData,
  type FrameMessageWithPixels,
  type HandleFrameMessage,
  type HandleInitialFrameMessage,
  hasFramePixels,
  isInitialFrameMessage,
  MAX_VOLUME_ALLOCATION_BYTES,
  computeVolumeBytes,
} from 'modules/viewer/workers/pixelDataSharedWorkerTypes';
import type { SupportedTextureConstructor, SupportedTextureTypes } from 'utils/textureUtils';
import type { FrameDataFragment } from 'generated/graphql';
import { logger } from 'modules/logger';
import { createVtkImageDataInstanceForFrame } from './utils';
import type { FullSingleLayerStack } from '../../../ViewportsConfigurations/types';
import { BaseSingleStackImagingProvider } from './BaseSingleStackImagingProvider';
import { safeDetachBuffer } from 'domains/viewer/Viewer/viewerUtils';

/**
 * Provides a progressive style implementation for loading images for a series.
 *
 * Fetches batches of instances from series as a tarball of `.dcm` files
 * When a batch is fetched, we resolve it into a vtkImageData, extract the scalar array, and insert it into a pixel buffer representing the entire series
 * We can start shwowing viewports as soon as wel have scalars, so for large datasets, we can provide interactivity much more quickly
 */

type ProgressiveJobPayload = undefined;

// Frames can stream in hundreds per second from cache; one canvas render per frame is wasted work.
export const FRAME_RENDER_INTERVAL_MS = 100;
// @ts-expect-error [EN-7967] - TS2344 - Type 'T' does not satisfy the constraint 'Readonly<FullBaseStack>'.
export type ProgressiveImagingProviderArgs<T> = BaseImagingProviderArgs<T> & {
  batchSize?: number;
};

export class ProgressiveImagingProvider<
  T extends FullSingleLayerStack = FullSingleLayerStack,
> extends BaseSingleStackImagingProvider {
  pixels: SupportedTextureTypes;
  minPixelValue: number;
  maxPixelValue: number;
  batchSize: number;
  rangeMap: Map<string, [number, number]> = new Map();
  _pixelMap: {
    [key: string]: SupportedTextureTypes;
  };
  _imageDataMap: {
    [key: string]: vtkImageData;
  };
  _frameLoadingStateMap: {
    [key: string]: Set<string>;
  } = {}; // what is actively in memory
  _framesLoadedToCache: Set<string>; // what has been downloaded (use this to track loading progress)
  #sliceScrollingOrigin: number;
  #updateSliceScrollingOrigin: (arg1: number) => void;
  #vtkImageMap: Map<string, vtkImageData>;
  #frameSmidToIndex: Map<string, number>;
  #requestRenderThrottled = throttle(
    () => this.viewRef.current?.requestRender?.(),
    FRAME_RENDER_INTERVAL_MS
  );

  constructor({
    batchSize = 20,
    // @ts-expect-error [EN-7967] - TS2339 - Property 'rerenderTriggerCount' does not exist on type 'ProgressiveImagingProviderArgs<T>'.
    rerenderTriggerCount = 100,
    ...baseImagingProviderArgs
  }: ProgressiveImagingProviderArgs<T>) {
    super(baseImagingProviderArgs);
    if (this._frameLoadingStateMap[baseImagingProviderArgs.stack.smid] == null) {
      this._frameLoadingStateMap[baseImagingProviderArgs.stack.smid] = new Set();
    }
    /**
     * The underlying typed array dataset for the series, rebuilt directly from the VTK images for each instance.
     */
    this.pixels = new Float32Array(0).fill(0);
    this._framesLoadedToCache = new Set();
    this._pixelMap = {};
    this._imageDataMap = {};
    /** Min + Max values for data in {this.pixels} */
    this.minPixelValue = 0;
    this.maxPixelValue = 0;
    /** Number of instances to fetch at once */
    this.batchSize = batchSize;
    // we are a progressive renderer
    this.type = 'progressive';
    this.unpackingMethod = 'tardah';
    this.#sliceScrollingOrigin = 0; // updated to initial slice on load
    this.#updateSliceScrollingOrigin = debounce(
      (newOrigin: number) => {
        this.#sliceScrollingOrigin = newOrigin;
      },
      50,
      {
        leading: true,
      }
    );
    this.#vtkImageMap = new Map();
    this.#frameSmidToIndex = new Map(
      baseImagingProviderArgs.stack.frames.map((frame, index) => [frame.smid, index])
    );
  }

  /** O(1) lookup from frame SMID to index in this stack's frame list */
  getFrameIndex(frameSmid: string): number | undefined {
    return this.#frameSmidToIndex.get(frameSmid);
  }

  isSopClass(sopClass: string): boolean {
    return this.stack.frames.every((frame) => frame.sopClassUID.startsWith(sopClass));
  }

  getVolume(): SupportedTextureTypes {
    return this.pixels;
  }

  getRange(): [number, number] | null {
    return this.minPixelValue !== 0 || this.maxPixelValue !== 0
      ? [this.minPixelValue, this.maxPixelValue]
      : null;
  }

  getFramesLoaded(): number {
    return this._frameLoadingStateMap[this.stack.smid].size;
  }

  getFramesLoadedToCache(): number {
    return this._framesLoadedToCache.size;
  }

  isFrameLoaded(frameSmid: string): boolean {
    return this._frameLoadingStateMap[this.stack.smid].has(frameSmid);
  }

  isReadyToRender(): boolean {
    return (
      this._frameLoadingStateMap[this.stack.smid].size > 0 &&
      (this.status === 'loading' || this.status === 'complete')
    );
  }

  getImage(frameSmid?: string | null): vtkImageData | null {
    if (this.is3Dable()) {
      return this.vtkImage;
    } else {
      if (frameSmid == null) return null;
      return this._imageDataMap[frameSmid] ?? null;
    }
  }

  getFramePixels(frameSmid: string): SupportedTextureTypes | null {
    // Non-3D-able stacks store pixels per-frame in _pixelMap
    const fromMap = this._pixelMap[frameSmid];
    if (fromMap != null) {
      return fromMap;
    }

    // Multi-object stacks need to extract individual frames from a 3Dable volume
    // to render as non-3Dable frames in the composite provider.
    if (this.is3Dable() && this.pixels.length > 0) {
      const frameIndex = this.#frameSmidToIndex.get(frameSmid);
      if (frameIndex != null) {
        const frameCount = this.stackSize;
        const pixelsPerFrame = Math.floor(this.pixels.length / frameCount);
        const startOffset = frameIndex * pixelsPerFrame;
        const endOffset = startOffset + pixelsPerFrame;
        return this.pixels.subarray(startOffset, endOffset);
      }
    }

    return null;
  }

  /**
   * Whether this provider would keep `frameIndex` resident in renderer memory under its current load
   * type. Read both when a delivered frame arrives (to decide whether to insert it) and by
   * `WorkerLoadedImagingProvider.handleFrame` (to decide whether a frame whose pixels were withheld
   * needs re-requesting), so the two can't drift.
   */
  _shouldLoadFrameIntoMemory(frameIndex: number): boolean {
    return (
      this.loadType === 'full' ||
      (this.loadType === 'initial' && frameIndex === this.getActiveSlice('TWO_D_DRE'))
    );
  }

  handleFrame(frameData: HandleFrameMessage | HandleInitialFrameMessage | FrameData): void {
    // VX-4462: drop a frame still in flight when this provider was torn down. dispose() has just detached the volume
    // and emptied the per-frame maps, so a late 3D-able frame would allocate a whole new volume (this.pixels is empty
    // again) and a late per-frame one would repopulate _pixelMap, pinning pixel memory to a dead provider until GC.
    if (this.isDisposed) return;
    const frameIndex = this.#frameSmidToIndex.get(frameData.frameSmid) ?? -1;

    // Check if the worker signaled that volume allocation failed for this stack
    if (
      isInitialFrameMessage(frameData) &&
      frameData.volumeAllocationFailed === true &&
      this.is3Dable()
    ) {
      logger.warn(
        '[PROGRESIVE IMAGING PROVIDER] worker signaled volume allocation failed; falling back to per-frame loading',
        { stackSmid: this.stack.smid }
      );
      this.overrideIs3DableToFalse();
      this._resetAvailableViewTypes();
    }

    // VX-4775: `pixels` is absent when the worker loaded this stack cache-only. The frame is on disk
    // and every counter below still advances, but there is nothing to insert — and nothing to insert
    // it into, since the worker also skipped the volume allocation.
    if (hasFramePixels(frameData) && this._shouldLoadFrameIntoMemory(frameIndex)) {
      if (this.is3Dable()) {
        this.#handle3DableFrame(frameData, frameIndex);
      } else {
        this.#handleNon3DableFrame(frameData);
      }
      this._frameLoadingStateMap[this.stack.smid].add(frameData.frameSmid);
    }

    this.fromCache = this.fromCache || frameData.fromCache;
    this.rangeMap.set(frameData.frameSmid, frameData.range);
    this.minPixelValue = Math.min(frameData.range[0], this.minPixelValue);
    this.maxPixelValue = Math.max(frameData.range[1], this.maxPixelValue);

    // dirty, but very fast way to get cool loading bars
    // Guard against Node/worker environments (and post-teardown timers in tests) where document
    // is not defined.
    if (typeof document !== 'undefined') {
      const loadingBars = document.getElementsByClassName(`${frameData.frameSmid}-loader`);
      for (let i = loadingBars.length - 1; i >= 0; i--) {
        // @ts-expect-error [EN-7967] - TS2339 - Property 'style' does not exist on type 'Element'.
        loadingBars[i].style.backgroundColor = colors.blue4;
      }
    }
    // keep track of loaded instances in case we need to render a progress bar mid-load
    this._framesLoadedToCache.add(frameData.frameSmid);
    this.emitter.emit(emitterEvents.frameLoaded, this.getFrameLoadedEvent());

    if (this._framesLoadedToCache.size === this.stack.frames.length) {
      // final slice: update status, emit event, and render view one last time
      if (this.status !== 'error') this.setStatus('complete');
      this.emitter.emit(emitterEvents.stackLoaded);
    }
    this.#requestRenderThrottled();
  }

  #handle3DableFrame(message: FrameMessageWithPixels, frameIndex: number) {
    const { pixels } = message;
    // Only the initial-frame message and the loader-rail FrameData carry a preallocated volume; a
    // plain `handle-frame` does not. `in` narrows across all three without a suppression.
    const volume = 'volume' in message ? message.volume : undefined;
    // first frame
    if (this.pixels.length === 0) {
      // in unusual reloading scenarios, we might have to rebuild the volume on the fly
      // there is a unit test for this specific scenario
      // @ts-expect-error [EN-7967] - TS2351 - This expression is not constructable.
      this.pixels = volume ?? new pixels.constructor(pixels.length * this.stack.frames.length);
      this.emitter.emit(emitterEvents.volumeUpdated);

      const image = this.vtkImage;
      const scalars = image?.getPointData().getScalars();
      if (scalars) {
        scalars.setData(this.pixels);
      } else {
        const dataArray = vtkDataArray.newInstance({
          name: 'Scalars',
          numberOfComponents: this.getNumberOfColorChannels(frameIndex),
          values: this.pixels,
        });
        image.getPointData().setScalars(dataArray);
      }

      // the vtkImage may not have been modified, but we have new image data
      // and need to let stuff rerender regardless
      this.triggerImageChangeCallbacks(frameIndex);
    } else if (
      (pixels.constructor as SupportedTextureConstructor).BYTES_PER_ELEMENT >
      (this.pixels.constructor as SupportedTextureConstructor).BYTES_PER_ELEMENT
    ) {
      // new images may arrive that are a different type
      // than what the volume initialized with, which is only based on the first frame's type
      // if so, we must build a larger volume and copy all the old data to the new, larger-typed volume
      // then emit an event to rerender existing viewports
      logger.info(`[PROGRESSIVE IMAGING PROVIDER] rebuilding volume for stack ${this.stack.smid}`);

      const incomingPixelsCtor = pixels.constructor as SupportedTextureConstructor;
      const totalBytes = computeVolumeBytes(
        incomingPixelsCtor.BYTES_PER_ELEMENT,
        pixels.length,
        this.stack.frames.length
      );
      if (totalBytes > MAX_VOLUME_ALLOCATION_BYTES) {
        // Upsized volume would exceed size limit — migrate existing frames and fall back to per-frame
        logger.warn(
          '[PROGRESIVE IMAGING PROVIDER] upsized volume would exceed allocation limit; falling back to per-frame loading',
          {
            stackSmid: this.stack.smid,
            totalBytes,
            maxVolumeAllocationBytes: MAX_VOLUME_ALLOCATION_BYTES,
          }
        );
        this.#downgradeToPerFrame(message);
        return;
      }
      let newVolume: SupportedTextureTypes;
      try {
        newVolume = new incomingPixelsCtor(pixels.length * this.stack.frames.length);
      } catch (error) {
        // A volume under the threshold can still throw RangeError under real memory pressure /
        // fragmentation. Fall back to per-frame loading, same as the threshold guard above.
        logger.warn(
          '[PROGRESIVE IMAGING PROVIDER] upsized volume allocation threw; falling back to per-frame loading',
          { stackSmid: this.stack.smid, totalBytes, error: String(error) }
        );
        this.#downgradeToPerFrame(message);
        return;
      }
      // set the values, not the bytes of the old array into the new higher-byte buffer
      // the TypedArray will convert each value to the new byte type.
      newVolume.set(this.pixels);
      const oldVolume = this.pixels.buffer;
      this.pixels = newVolume;
      this.emitter.emit(emitterEvents.volumeUpdated);

      const scalars = this.vtkImage?.getPointData().getScalars();
      if (scalars) {
        scalars.setData(this.pixels);
      }

      this.triggerImageChangeCallbacks(frameIndex);
      // drop the old buffer
      safeDetachBuffer(oldVolume);
    }

    this.pixels.set(pixels, frameIndex * pixels.length);

    safeDetachBuffer(pixels.buffer);
  }

  /**
   * Abandon the contiguous 3D volume and fall back to per-frame storage for the rest of this
   * provider's life. Ordering matters: flip is3Dable() to false and reset the available view types
   * BEFORE migrating, so the image-change callbacks fired during migration resolve to per-frame
   * images (getImage() routes on is3Dable()). Finally route the frame that triggered the downgrade
   * through the per-frame path.
   */
  #downgradeToPerFrame(message: FrameMessageWithPixels): void {
    this.overrideIs3DableToFalse();
    this._resetAvailableViewTypes();
    this.#migrateVolumeToPerFrame();
    this.#handleNon3DableFrame(message);
  }

  /**
   * Build a per-frame vtkImageData for `frameSmid`, set its scalars, and record it in the per-frame
   * maps. Shared by the per-frame display path (#handleNon3DableFrame) and the volume→per-frame
   * migration so the channel count and the maps can't drift. Returns the scalar array so callers
   * can reuse the same instance to also back the shared vtkImage.
   */
  #storePerFramePixels(
    frameSmid: string,
    tag: FrameDataFragment,
    pixels: SupportedTextureTypes,
    numberOfComponents: number
  ) {
    const image = this.#vtkImageMap.get(frameSmid) ?? createVtkImageDataInstanceForFrame(tag);
    if (!this.#vtkImageMap.has(frameSmid)) {
      this.#vtkImageMap.set(frameSmid, image);
    }
    const dataArray = vtkDataArray.newInstance({
      name: 'Scalars',
      numberOfComponents,
      values: pixels,
    });
    image.getPointData().setScalars(dataArray);
    this._pixelMap[frameSmid] = pixels;
    this._imageDataMap[frameSmid] = image;
    return { image, dataArray };
  }

  /** Migrate frames from the contiguous volume buffer to per-frame storage */
  #migrateVolumeToPerFrame(): void {
    if (this.pixels.length === 0) return;

    const frameCount = this.stack.frames.length;
    const pixelsPerFrame = Math.floor(this.pixels.length / frameCount);
    const loadedFrames = this._frameLoadingStateMap[this.stack.smid];
    // A frame counted as loaded but not stored renders as a blank "complete" slice, so any frame we
    // can't migrate is dropped from the loaded set (collected here, deleted after iterating to avoid
    // mutating the set we're iterating).
    const unmigratableFrames: string[] = [];

    for (const frameSmid of loadedFrames) {
      const frameIndex = this.#frameSmidToIndex.get(frameSmid);
      if (frameIndex == null) {
        logger.warn(
          '[PROGRESIVE IMAGING PROVIDER] no frame index during volume→per-frame migration; dropping from loaded set',
          { stackSmid: this.stack.smid, frameSmid }
        );
        unmigratableFrames.push(frameSmid);
        continue;
      }

      const tag = this.frameTags[frameIndex];
      if (!tag) {
        logger.warn(
          '[PROGRESIVE IMAGING PROVIDER] no frame tag during volume→per-frame migration; dropping from loaded set',
          { stackSmid: this.stack.smid, frameSmid, frameIndex }
        );
        unmigratableFrames.push(frameSmid);
        continue;
      }

      // Copy frame pixels out of the contiguous volume into a standalone array. Match the channel
      // count the 3D volume was built with (see #handle3DableFrame) so color / multi-component
      // frames aren't reinterpreted as single-component scalars.
      const start = frameIndex * pixelsPerFrame;
      const framePixels = this.pixels.slice(start, start + pixelsPerFrame);
      this.#storePerFramePixels(
        frameSmid,
        tag,
        framePixels,
        this.getNumberOfColorChannels(frameIndex)
      );
    }

    // Maintain the invariant that every frame counted as loaded is also stored.
    for (const frameSmid of unmigratableFrames) {
      loadedFrames.delete(frameSmid);
    }

    // Release the GC pin the shared 3D vtkImage holds on the old volume buffer before we drop it.
    const sharedScalars = this.vtkImage?.getPointData().getScalars();
    if (sharedScalars) {
      sharedScalars.setData(new Uint8Array(0));
    }

    // Release the contiguous volume buffer and notify volume consumers it's gone
    // (useImagingProviderVolume only refreshes on volumeUpdated).
    safeDetachBuffer(this.pixels.buffer);
    this.pixels = new Float32Array(0);
    this.emitter.emit(emitterEvents.volumeUpdated);

    // Refresh on-screen slices. is3Dable() is already false (caller flips it before migrating), so
    // triggerImageChangeCallbacks resolves the per-frame images we just stored rather than the
    // now-released volume.
    for (const frameSmid of loadedFrames) {
      const frameIndex = this.#frameSmidToIndex.get(frameSmid);
      if (frameIndex != null) {
        this.triggerImageChangeCallbacks(frameIndex);
      }
    }
  }

  #handleNon3DableFrame({ pixels, frameSmid }: FrameMessageWithPixels) {
    const firstTagIndex = this.#frameSmidToIndex.get(frameSmid) ?? -1;
    const firstTag = this.frameTags[firstTagIndex];
    if (!firstTag) return;

    // labelmap with single component — the per-frame display path stays single-component on purpose
    const { dataArray } = this.#storePerFramePixels(frameSmid, firstTag, pixels, 1);
    this.vtkImage?.getPointData().setScalars(dataArray);
    this.triggerImageChangeCallbacks(firstTagIndex);
  }

  /******************************************************************
   * Overrides
   * handle scrolling when data is not always available
   ******************************************************************/

  async load(
    {
      initialSlice = 0,
      priority,
    }: {
      initialSlice?: number;
      priority: number;
    } = { priority: 0 }
  ): Promise<unknown> {
    this.#sliceScrollingOrigin = initialSlice;
    return super.load({ initialSlice, priority });
  }

  setActiveSlice(viewType: DreAllViewportTypes, viewportId: string, slice: number): void {
    // @ts-expect-error [EN-7967] - TS2367 - This comparison appears to be unintentional because the types 'Set<string>' and 'number' have no overlap.
    if (this._frameLoadingStateMap.size === this.stackSize) {
      return super.setActiveSlice(viewType, viewportId, slice);
    }

    const previousSlice = this.getActiveSlice(viewType, viewportId);
    this.#updateSliceScrollingOrigin(previousSlice);

    const frameSmid = this.frameSmidsMap[slice];
    if (this._frameLoadingStateMap[this.stack.smid].has(frameSmid)) {
      return super.setActiveSlice(viewType, viewportId, slice);
    }

    if (this.is3Dable() && Math.abs(this.#sliceScrollingOrigin - slice) > 1) {
      // scrolling quickly, see if we can find a fallback slice to render instead
      for (let offsetIndex = 0; offsetIndex < OFFSETS.length; offsetIndex++) {
        const offsetFrameSmid = this.frameSmidsMap[slice + OFFSETS[offsetIndex]];
        if (this._frameLoadingStateMap[this.stack.smid].has(offsetFrameSmid)) {
          return super.setActiveSlice(viewType, viewportId, slice + OFFSETS[offsetIndex]);
        }
      }
    }

    analytics.track(viewer.usr.scrolledToLoadingSlice, {
      stackSmid: this.stack.smid,
      slice,
      previousSlice,
      percentLoaded:
        this._frameLoadingStateMap[this.stack.smid].size / Object.keys(this.frameSmidsMap).length,
      viewType,
      viewportId,
    });

    return super.setActiveSlice(viewType, viewportId, slice);
  }

  unloadAllFrames(): void {
    if (this.is3Dable()) {
      this.unloadVolume();
    } else {
      this.stack.frames.forEach((frame, frameIndex) => {
        this.unloadFrameFromMemory(frameIndex);
      });
    }
    this.#vtkImageMap.clear();
  }

  dispose(): void {
    super.dispose();
    this.#requestRenderThrottled.cancel();
    // Cancel the pending leading-debounce so the slice-scrolling timer can't fire after teardown.
    (this.#updateSliceScrollingOrigin as { cancel?: () => void }).cancel?.();
    this.unloadAllFrames();
    const scalars = this.vtkImage?.getPointData().getScalars();
    if (scalars) {
      scalars.setData(new Uint8Array(0));
    }
    this.rangeMap.clear();
    this._frameLoadingStateMap[this.stack.smid]?.clear();
    this._framesLoadedToCache.clear();
    this.minPixelValue = 0;
    this.maxPixelValue = 0;
  }

  unloadVolume() {
    safeDetachBuffer(this.pixels.buffer);
    this.pixels = new Uint8Array(0);
    this.emitter.emit(emitterEvents.volumeUpdated);
    this._frameLoadingStateMap[this.stack.smid] = new Set();

    // remove references to old pixel data
    const image = this.vtkImage;
    const scalars = image?.getPointData().getScalars();
    if (scalars) {
      scalars.setData(this.pixels);
    } else {
      // dummy instance
      const dataArray = vtkDataArray.newInstance({
        name: 'Scalars',
        numberOfComponents: this.getNumberOfColorChannels(0),
        values: this.pixels,
      });
      image.getPointData().setScalars(dataArray);
    }
  }

  unloadFrameFromMemory(frameIndex: number) {
    const frame = this.stack.frames[frameIndex];
    if (
      this._frameLoadingStateMap[this.stack.smid] != null &&
      this._frameLoadingStateMap[this.stack.smid].has(frame.smid)
    ) {
      if (this.is3Dable()) {
        // fallback to full-volume unload if single-frame unload is called
        // but prefer calling `unloadVolume` in the first place
        logger.error(
          '[PROGRESSIVE IMAGING PROVIDER] `unloadFrameFromMemory` called on 3dable stack. Use `unloadVolume` instead.'
        );
        return;
      } else {
        const image = this._imageDataMap[frame.smid] ?? this.#vtkImageMap.get(frame.smid);
        if (image != null) {
          image.getPointData().setScalars(
            // dummy instance
            vtkDataArray.newInstance({
              name: 'Scalars',
              numberOfComponents: this.getNumberOfColorChannels(frameIndex),
              values: new Uint8Array(0),
            })
          );
        }
        this._frameLoadingStateMap[this.stack.smid].delete(frame.smid);
        safeDetachBuffer(this._pixelMap[frame.smid]?.buffer);
        delete this._pixelMap[frame.smid];
        delete this._imageDataMap[frame.smid];
        this.#vtkImageMap.delete(frame.smid);
      }
    }
  }
  // Get the min / max pixel range for a specific slice
  getRangeForSlice(slice: number): [number, number] | null {
    const frameSmid = this.frameSmidsMap[slice];
    return this.rangeMap.get(frameSmid) ?? null;
  }
}

const OFFSETS = [1, -1, 2, -2];

export const sliceEmissionJobs = new LRUMap<string, Promise<ProgressiveJobPayload>>(20);
